import { Link as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
// components
import { Seo } from '../components/seo';

// ----------------------------------------------------------------------

const ILLUSTRATION = '/assets/illustrations/illustration_404.svg';

// This page renders inside SimpleLayout, which already supplies the header and
// the footer, so the block is sized to leave room for both instead of claiming a
// full viewport of its own and pushing the footer below the fold.
const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 560,
  margin: 'auto',
  minHeight: '70vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(5, 0),
}));

// ----------------------------------------------------------------------

export default function Page404() {
  // The router renders this for any unmatched path, so the location is the
  // URL the visitor actually asked for. Echoing it back tells them whether the
  // link was mistyped or whether the page moved.
  const { pathname } = useLocation();
  const requested = typeof pathname === 'string' ? pathname.trim() : '';

  return (
    <>
      {/* A 404 must never be indexed. robots.txt also disallows /404, and this
          covers every other dead URL that reaches this page. */}
      <Seo
        title="Page Not Found"
        description="That page is no longer available. Browse ASAP licensing exam preparation courses, services and exam tips instead."
        path={requested || '/404'}
        robots="noindex, follow"
      />

      <Container>
        <StyledContent sx={{ textAlign: 'center', alignItems: 'center' }}>
          <Typography variant="h3" paragraph>
            Sorry, page not found!
          </Typography>

          <Typography sx={{ color: 'text.secondary', mb: 4 }}>
            We could not find{' '}
            {requested ? <Box component="span" sx={{ fontWeight: 700 }}>{requested}</Box> : 'the page you requested'}.
            The address may be mistyped, or the page may have moved.
          </Typography>

          <Box
            component="img"
            src={ILLUSTRATION}
            alt=""
            aria-hidden="true"
            sx={{ width: '100%', maxWidth: 320, height: 'auto', mx: 'auto', mb: { xs: 4, sm: 6 } }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'center', flexWrap: 'wrap', rowGap: 2 }}
          >
            <Button to="/" size="large" variant="contained" component={RouterLink}>
              Go to Home
            </Button>
            <Button to="/courses" size="large" variant="outlined" component={RouterLink}>
              Browse Courses
            </Button>
            <Button to="/contact-us" size="large" variant="outlined" component={RouterLink}>
              Contact Us
            </Button>
          </Stack>
        </StyledContent>
      </Container>
    </>
  );
}